import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, fmtINR, fmtDate } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { UploadSimple, Robot, CheckCircle, ArrowsClockwise, X, ClockCounterClockwise, FolderOpen } from "@phosphor-icons/react";
import { SearchableSelect } from "../components/SearchableSelect";
import { LedgerFormModal } from "../components/LedgerFormModal";

export default function BankReconciliationPage() {
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [session, setSession] = useState(null);
  const [selections, setSelections] = useState({}); // txn_index -> {kind, target_id} | null (excluded)
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [review, setReview] = useState(false);
  const [newLedger, setNewLedger] = useState(null); // { i, side, name }
  const loadLedgers = () => api.get("/accounting/ledgers").then(r => setLedgers(r.data)).catch(err => console.error("Ledgers load failed:", err));
  useEffect(() => { loadLedgers(); }, []);
  const ledgerOpts = ledgers.map(l => ({ value: l.id, label: l.name, sub: l.group_name, keywords: [l.group_name] }));
  const ledgerName = (id) => ledgers.find(l => l.id === id)?.name || "—";
  const bankId = ledgers.find(l => l.name === "Bank A/c")?.id, cashId = ledgers.find(l => l.name === "Cash A/c")?.id;

  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const loadHistory = () => api.get("/reconciliation/sessions").then(r => setHistory(r.data)).catch(err => console.error("Recon history load failed:", err));
  useEffect(() => { loadHistory(); }, []);
  const applySession = (data) => {
    setSession(data);
    const initial = {};
    (data.transactions || []).forEach((t, i) => {
      if ((data.confirmed || []).includes(i)) return;
      if (t.match) initial[i] = { kind: t.match.kind, target_id: t.match.id, mode: "Bank" };
      else if (t.suggestion) initial[i] = { kind: t.suggestion.kind, dr_ledger_id: t.suggestion.dr_ledger_id, cr_ledger_id: t.suggestion.cr_ledger_id };
    });
    setSelections(initial);
    setResult(null);
    setShowHistory(false);
  };
  const openSession = async (id) => {
    try { const { data } = await api.get(`/reconciliation/sessions/${id}`, { timeout: 60000 }); await loadLedgers(); applySession(data); toast.success(`Reopened ${data.filename}`); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const upload = async (file, force = false) => {
    if (!file) return;
    setAnalyzing(true);
    setSession(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/reconciliation/upload${force ? "?force=1" : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      await loadLedgers(); // suggestions may have created ledgers like "Bank Charges"
      applySession(data);
      loadHistory();
      toast.success(`AI analysed ${data.transactions?.length || 0} transactions`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.code === "not_statement") {
        if (window.confirm(`${detail.message}\n\nUpload anyway and let AI try?`)) { setAnalyzing(false); return upload(file, true); }
        return;
      }
      toast.error(typeof detail === "object" && detail?.message ? detail.message : (formatApiError(detail) || e.message));
    } finally {
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setManualMatch = (i, value) => {
    if (!value) {
      setSelections(s => { const n = { ...s }; delete n[i]; return n; });
      return;
    }
    const [kind, target_id] = value.split("::");
    if (kind === "journal" || kind === "contra") {
      const t = txns[i], sg = t.suggestion && t.suggestion.kind === kind ? t.suggestion : null;
      const dflt = kind === "contra" ? (t.type === "debit" ? { dr_ledger_id: cashId, cr_ledger_id: bankId } : { dr_ledger_id: bankId, cr_ledger_id: cashId })
        : (t.type === "debit" ? { dr_ledger_id: "", cr_ledger_id: bankId } : { dr_ledger_id: bankId, cr_ledger_id: "" });
      setSelections(s => ({ ...s, [i]: { kind, dr_ledger_id: sg?.dr_ledger_id || dflt.dr_ledger_id || "", cr_ledger_id: sg?.cr_ledger_id || dflt.cr_ledger_id || "" } }));
      return;
    }
    setSelections(s => ({ ...s, [i]: { kind, target_id, mode: s[i]?.mode || "Bank" } }));
  };
  const patchSel = (i, patch) => setSelections(s => ({ ...s, [i]: { ...s[i], ...patch } }));
  const pendingItems = () => Object.entries(selections)
    .filter(([i]) => !(session?.confirmed || []).includes(Number(i)))
    .map(([i, v]) => ({ txn_index: Number(i), ...v }));
  // Dr / Cr heads for the review table — mirrors how the backend posts each kind
  const headsOf = (t, v) => {
    const money = v.mode === "Cash" ? "Cash A/c" : "Bank A/c";
    const target = (t.type === "credit" ? session.open_invoices : session.open_purchases)?.find(o => o.id === v.target_id);
    if (v.kind === "invoice") return { dr: money, cr: target?.party || "Customer", label: "Receipt" };
    if (v.kind === "purchase") return { dr: target?.party || "Vendor", cr: money, label: "Payment" };
    return { dr: ledgerName(v.dr_ledger_id), cr: ledgerName(v.cr_ledger_id), label: v.kind === "contra" ? "Contra" : "Journal" };
  };

  const openReview = () => {
    const items = pendingItems();
    if (items.length === 0) return toast.error("Nothing selected to record");
    const bad = items.find(it => (it.kind === "journal" || it.kind === "contra") && (!it.dr_ledger_id || !it.cr_ledger_id || it.dr_ledger_id === it.cr_ledger_id));
    if (bad) return toast.error(`Row ${bad.txn_index + 1}: choose different Debit and Credit ledgers`);
    setReview(true);
  };
  const confirm = async () => {
    const items = pendingItems();
    setReview(false);
    setConfirming(true);
    try {
      const { data } = await api.post("/reconciliation/confirm", { session_id: session.id, items });
      setResult(data);
      setSession(s => ({ ...s, confirmed: [...(s.confirmed || []), ...items.map(it => it.txn_index)] }));
      toast.success(`${data.recorded} entr${data.recorded === 1 ? "y" : "ies"} posted`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setConfirming(false);
    }
  };

  const txns = session?.transactions || [];
  const matched = txns.filter(t => t.match).length;

  return (
    <div data-testid="bank-recon-page">
      <PageHeader
        title="Bank Reconciliation (AI)"
        subtitle="Upload your HDFC / Axis / Yes Bank statement (PDF, CSV or Excel). AI matches receipts to invoices and payments to purchases — you confirm."
      />
      <div className="p-8 max-w-6xl space-y-6">
        <div className="card-base p-5 flex items-center gap-4 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf,.csv,.xls,.xlsx,.txt" className="hidden" data-testid="recon-file-input" onChange={e => upload(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={analyzing} className="btn-primary inline-flex items-center gap-1.5" data-testid="recon-upload-btn">
            {analyzing ? <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
            {analyzing ? "AI analysing statement… (up to a minute)" : "Upload Bank Statement"}
          </button>
          <div className="text-xs text-ink-muted flex items-center gap-1.5"><Robot size={14} /> Money IN → matched to open invoices · Money OUT → matched to purchases / inward bills</div>
          <button onClick={() => setShowHistory(h => !h)} className="btn-secondary inline-flex items-center gap-1.5 ml-auto" data-testid="recon-history-toggle">
            <ClockCounterClockwise size={14} /> History ({history.length})
          </button>
        </div>

        {(showHistory || (!session && history.length > 0)) && (
          <div className="card-base p-0 overflow-hidden" data-testid="recon-history">
            <div className="px-5 py-3 border-b border-border font-display font-semibold">Past statements</div>
            <div className="table-wrapper border-0 rounded-none">
              <table className="table-base">
                <thead><tr><th>Uploaded</th><th>File</th><th className="text-right">Rows</th><th className="text-right">Posted</th><th className="text-right">Money In</th><th className="text-right">Money Out</th><th>By</th><th></th></tr></thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} data-testid={`recon-history-row-${h.id}`} className={session?.id === h.id ? "bg-surface-muted" : ""}>
                      <td className="tabular text-xs whitespace-nowrap">{fmtDate(h.created_at)} <span className="text-ink-muted">{new Date(h.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></td>
                      <td className="text-xs font-mono">{h.filename}</td>
                      <td className="text-right tabular">{h.rows}</td>
                      <td className="text-right tabular"><span className={`status-pill ${h.posted === h.rows && h.rows > 0 ? "status-success" : h.posted > 0 ? "status-info" : "status-neutral"}`}>{h.posted}/{h.rows}</span></td>
                      <td className="text-right tabular text-xs">{fmtINR(h.credits)}</td><td className="text-right tabular text-xs">{fmtINR(h.debits)}</td>
                      <td className="text-xs text-ink-muted">{h.uploaded_by}</td>
                      <td className="text-right"><button onClick={() => openSession(h.id)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`recon-open-${h.id}`}><FolderOpen size={13} /> Open</button></td>
                    </tr>
                  ))}
                  {history.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-ink-muted">No statements uploaded yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {session && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi label="Transactions" value={txns.length} />
              <Kpi label="AI Matched" value={matched} accent />
              <Kpi label="Unmatched" value={txns.length - matched} />
              <Kpi label="Recorded" value={(session.confirmed || []).length} />
            </div>

            <div className="card-base p-0 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <div className="font-display font-semibold">{session.filename}</div>
                <button onClick={openReview} disabled={confirming} className="btn-primary inline-flex items-center gap-1.5" data-testid="recon-confirm-btn">
                  <CheckCircle size={14} weight="bold" /> {confirming ? "Posting…" : `Review & Post (${pendingItems().length})`}
                </button>
              </div>
              <div className="table-wrapper border-0 rounded-none">
                <table className="table-base">
                  <thead><tr>
                    <th>Date</th><th>Narration</th><th className="text-right">Amount</th><th>Type</th><th className="min-w-[320px]">Post as</th><th className="min-w-[300px]">Dr / Cr ledgers</th><th>AI</th>
                  </tr></thead>
                  <tbody>
                    {txns.map((t, i) => {
                      const done = (session.confirmed || []).includes(i);
                      const sel = selections[i];
                      const options = t.type === "credit" ? session.open_invoices : session.open_purchases;
                      const optKind = t.type === "credit" ? "invoice" : "purchase";
                      return (
                        <tr key={i} data-testid={`recon-row-${i}`} className={done ? "opacity-50" : ""}>
                          <td className="tabular text-xs whitespace-nowrap">{t.date}</td>
                          <td className="text-xs max-w-[320px]">{t.narration}{t.ref ? <div className="font-mono text-[10px] text-ink-muted">{t.ref}</div> : null}</td>
                          <td className="text-right tabular font-semibold">{fmtINR(t.amount)}</td>
                          <td><span className={`status-pill ${t.type === "credit" ? "status-success" : "status-info"}`}>{t.type === "credit" ? "IN" : "OUT"}</span></td>
                          <td>
                            {done ? (
                              <span className="text-xs font-semibold text-[#1C4F32]">✓ Posted</span>
                            ) : (
                              <select
                                className="field !py-1.5 text-xs"
                                data-testid={`recon-match-${i}`}
                                value={sel ? (sel.kind === "journal" || sel.kind === "contra" ? `${sel.kind}::` : `${sel.kind}::${sel.target_id}`) : ""}
                                onChange={e => setManualMatch(i, e.target.value)}
                              >
                                <option value="">— Skip —</option>
                                <optgroup label={t.type === "credit" ? "Receipt against invoice" : "Payment against purchase"}>
                                  {(options || []).map(o => (
                                    <option key={o.id} value={`${optKind}::${o.id}`}>
                                      {o.number} · {o.party} · {fmtINR(o.balance_due ?? o.total)}
                                    </option>
                                  ))}
                                </optgroup>
                                <optgroup label="Other entries">
                                  <option value="journal::">Journal — bank charges / interest / expense / income</option>
                                  <option value="contra::">Contra — cash ↔ bank</option>
                                </optgroup>
                              </select>
                            )}
                          </td>
                          <td className="text-xs">
                            {!done && sel && (sel.kind === "invoice" || sel.kind === "purchase") && (
                              <div className="space-y-1" data-testid={`recon-heads-${i}`}>
                                <select className="field !py-1 text-xs w-32" value={sel.mode || "Bank"} onChange={e => patchSel(i, { mode: e.target.value })} data-testid={`recon-mode-${i}`}>
                                  <option value="Bank">via Bank A/c</option><option value="Cash">via Cash A/c</option>
                                </select>
                                <div className="text-ink-muted"><b>Dr</b> {headsOf(t, sel).dr} · <b>Cr</b> {headsOf(t, sel).cr}</div>
                              </div>
                            )}
                            {!done && sel && (sel.kind === "journal" || sel.kind === "contra") && (
                              <div className="space-y-1" data-testid={`recon-heads-${i}`}>
                                <SearchableSelect testId={`recon-dr-${i}`} placeholder="Debit ledger…" value={sel.dr_ledger_id || ""} options={ledgerOpts}
                                  onChange={v => patchSel(i, { dr_ledger_id: v })} onCreateNew={(name) => setNewLedger({ i, side: "dr", name })} />
                                <SearchableSelect testId={`recon-cr-${i}`} placeholder="Credit ledger…" value={sel.cr_ledger_id || ""} options={ledgerOpts}
                                  onChange={v => patchSel(i, { cr_ledger_id: v })} onCreateNew={(name) => setNewLedger({ i, side: "cr", name })} />
                                {t.suggestion && <div className="text-[10px] text-ink-muted inline-flex items-center gap-1"><Robot size={11} /> {t.suggestion.reason}</div>}
                              </div>
                            )}
                            {!done && !sel && <span className="text-ink-muted">—</span>}
                          </td>
                          <td className="text-xs">
                            {t.match ? (
                              <span title={t.match.reason}><b>{t.match.confidence}%</b> · {t.match.reason?.slice(0, 60)}</span>
                            ) : (
                              <span className="text-ink-muted">{t.suggestion ? "suggested" : "no match"}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {txns.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-ink-muted">No transactions detected in this file.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {result && (
              <div className="card-base p-4 text-sm" data-testid="recon-result">
                <b>{result.recorded}</b> entr{result.recorded === 1 ? "y" : "ies"} posted: {result.details.map(d => `${d.number}${d.dr ? ` [${d.dr} → ${d.cr}]` : ""} (${fmtINR(d.amount)})`).join(", ")}
              </div>
            )}
            {review && (
              <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReview(false)} data-testid="recon-review-modal">
                <div className="bg-white rounded-lg w-full max-w-3xl shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div className="font-display font-semibold">Confirm ledger postings</div>
                    <button onClick={() => setReview(false)} className="btn-ghost" data-testid="recon-review-close"><X size={16} /></button>
                  </div>
                  <div className="p-5 overflow-y-auto">
                    <div className="text-xs text-ink-muted mb-3">Each row below becomes a voucher. Check the Debit and Credit heads before posting.</div>
                    <div className="table-wrapper">
                      <table className="table-base text-xs">
                        <thead><tr><th>Date</th><th>Narration</th><th>Entry</th><th>Debit</th><th>Credit</th><th className="text-right">Amount</th></tr></thead>
                        <tbody>
                          {pendingItems().map(it => { const t = txns[it.txn_index]; const h = headsOf(t, it); return (
                            <tr key={it.txn_index} data-testid={`recon-review-row-${it.txn_index}`}>
                              <td className="tabular whitespace-nowrap">{t.date}</td><td className="max-w-[220px] truncate" title={t.narration}>{t.narration}</td>
                              <td><span className="status-pill status-neutral">{h.label}</span></td>
                              <td className="font-semibold">Dr {h.dr}</td><td>Cr {h.cr}</td>
                              <td className="text-right tabular font-semibold">{fmtINR(t.amount)}</td>
                            </tr>); })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
                    <button onClick={() => setReview(false)} className="btn-secondary" data-testid="recon-review-cancel">Back</button>
                    <button onClick={confirm} disabled={confirming} className="btn-primary inline-flex items-center gap-1.5" data-testid="recon-review-post"><CheckCircle size={14} weight="bold" /> Post {pendingItems().length} voucher{pendingItems().length !== 1 ? "s" : ""}</button>
                  </div>
                </div>
              </div>
            )}
            {newLedger && <LedgerFormModal initial={{ name: newLedger.name || "" }} onClose={() => setNewLedger(null)}
              onSaved={async (l) => { await loadLedgers(); patchSel(newLedger.i, newLedger.side === "dr" ? { dr_ledger_id: l.id } : { cr_ledger_id: l.id }); setNewLedger(null); }} />}
          </>
        )}
      </div>
      <FieldStyle />
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`card-base p-4 ${accent ? "bg-brand text-white" : ""}`}>
      <div className={`label-overline mb-1 ${accent ? "text-white/70" : ""}`}>{label}</div>
      <div className="font-display text-2xl font-bold tabular">{value}</div>
    </div>
  );
}
